"use client";

import { PROFILE_API, STATS_API } from "@/constants/env";
import useAuthFetch from "@/hooks/use-auth-fetch";
import { Currency } from "@/types/currency";
import { useParams } from "next/navigation";
import { ReactNode, createContext, useContext } from "react";
import useSWR from "swr";

export type Vendor = {
  logo?: string;
  name?: string;
  vendorId: string;
  currency?: Currency;
  siteUrl?: string;
  country?: string;
};

type VendorListResponse = {
  data: Vendor[];
};
const MULTI_VENDOR_KEY = "multi"; // iit will be shown in the URL, so it should be a readable key
type ProviderProps = {
  children: ReactNode;
};

type VendorContentType = {
  multiVendorKey: string;
  vendors?: string[];
  vendorsData?: Vendor[];
  selectedVendor?: string;
  getVendorSettings: (vendor?: string) => Vendor | undefined;
  getVendorStyles: () => any;
};

const VendorContentContext = createContext<VendorContentType>({
  multiVendorKey: MULTI_VENDOR_KEY,
  getVendorSettings: () => undefined,
  getVendorStyles: () => undefined,
});

export const VendorContentProvider = ({ children }: ProviderProps) => {
  const params = useParams();
  const { fetcher, isReady } = useAuthFetch();

  const vendor = params?.vendor as string | undefined;

  const { data: vendorListData, error: vendorListError } =
    useSWR<VendorListResponse>(
      isReady && `${STATS_API}/permissionsVendorsList`,
      fetcher,
    );
  if (vendorListError) {
    console.error("Error fetching vendor list:", vendorListError);
  }
  const { data: vendorStyles } = useSWR<VendorListResponse>(
    isReady &&
      vendor &&
      vendor !== MULTI_VENDOR_KEY &&
      `${PROFILE_API}/vendors/${vendor}/style`,
    fetcher,
  );
  let vendorList = vendorListData?.data;

  let vendors = vendorList?.map((v) => v.vendorId);

  // this is getting a bit hacky...
  if (vendors?.includes("*")) {
    if (vendorList) {
      vendors = vendorList.map((v) => v.vendorId);
    }
  }

  // sort vendors alphabetically
  vendors?.sort((a, b) => a.localeCompare(b));
  vendorList?.sort((a, b) => a.vendorId.localeCompare(b.vendorId));
  // add multi vendor key if there are more than one vendor
  if (vendors && vendors?.length > 1) {
    if (!vendors.includes(MULTI_VENDOR_KEY)) {
      vendors = [MULTI_VENDOR_KEY, ...vendors];
    }
    if (
      vendorList &&
      !vendorList.find((v) => v.vendorId === MULTI_VENDOR_KEY)
    ) {
      vendorList = [
        {
          vendorId: MULTI_VENDOR_KEY,
          name: "Vendors overview",
        },
        ...vendorList,
      ];
    }
  }

  const selectedVendor = vendor;

  const getVendorSettings = (vendor?: string) => {
    if (!vendor || !vendorList) {
      return undefined;
    }
    return vendorList.find((v) => v.vendorId === vendor);
  };

  const getVendorStyles = () => {
    if (!vendorList) return {};
    return vendorStyles || {};
  };

  return (
    <VendorContentContext.Provider
      value={{
        multiVendorKey: MULTI_VENDOR_KEY,
        selectedVendor,
        vendors,
        vendorsData: vendorList,
        getVendorSettings,
        getVendorStyles,
      }}
    >
      {children}
    </VendorContentContext.Provider>
  );
};

export const useVendorContext = () => useContext(VendorContentContext);
